import { microServicesRoutes } from '@constants';
import {
  type ApiResponse,
  type CreateNumberDto,
  type NumberPool,
  type NumbersListDto,
  type UpdateNumberV3Payload,
} from '@types';
import { edgeApi } from '@lib/apiClients.ts';
import { logError } from '@lib/logger/logError.ts';
import { extractErrorMessage } from '@lib/logger/extractErrorMessage';

export default function useNumbersEdgeApi (): {
  getNumbersAsync: (
    pageNumber: number,
    pageLimit: number,
    search: string,
    filters: string[],
  ) => Promise<ApiResponse<NumberPool[]>>;
  deleteNumberAsync: (numberId: string) => Promise<ApiResponse<NumberPool>>;
  createNumberAsync: (numberData: CreateNumberDto) => Promise<ApiResponse<NumberPool>>;
  updateNumberV3Async: (numberData: UpdateNumberV3Payload) => Promise<ApiResponse<NumberPool>>;
  } {
  const handleError = <T = never>(
    message: string,
    error: unknown,
    source: string,
  ): ApiResponse<T> => {
    const errorMessage = extractErrorMessage(error);

    void logError(
      message,
      source,
      {
        error: errorMessage,
        rawError: error,
      },
      true,
    );

    return {
      success: false,
      error: {
        message,
        raw: errorMessage,
      },
    };
  };

  const getNumbersAsync = async (
    pageNumber: number,
    pageLimit: number,
    search: string,
    filters: string[],
  ): Promise<ApiResponse<NumberPool[]>> => {
    try {
      const res = await edgeApi.get<NumbersListDto>(
        microServicesRoutes.edge.get.numbers.numbersNest,
        { params: { page: pageNumber, pageLimit, search, filters } },
      );

      return {
        success: true,
        data: res.data?.data ?? [],
        totalPages: res.data?.totalPages,
        totalCount: res.data?.totalCount,
        pageNumber: res.data?.pageNumber,
      };
    } catch (e) {
      return handleError<NumberPool[]>('Error trying to get numbers', e, 'Edge getNumbers');
    }
  };

  const createNumberAsync = async (
    numberData: CreateNumberDto
  ): Promise<ApiResponse<NumberPool>> => {
    try {
      const res = await edgeApi.post<NumberPool>(
        microServicesRoutes.edge.post.numbers.numbersNest,
        numberData,
      );
      return { success: true, data: res.data };
    } catch (e) {
      return handleError<NumberPool>('Error trying to create number', e, 'Edge postNumber');
    }
  };

  const deleteNumberAsync = async (numberId: string): Promise<ApiResponse<NumberPool>> => {
    try {
      const res = await edgeApi.delete<NumberPool>(
        `${microServicesRoutes.edge.delete.numbers.numbersNest}/${numberId}`,
      );

      return { success: true, data: res.data };
    } catch (e) {
      return handleError<NumberPool>('Error trying to delete number', e, 'Edge deleteNumber');
    }
  };

  const updateNumberV3Async = async (
    numberData: UpdateNumberV3Payload,
  ): Promise<ApiResponse<NumberPool>> => {
    try {
      const res = await edgeApi.put<NumberPool>(
        `${microServicesRoutes.edge.put.numbers.numbersNest}/${numberData.id}`,
        numberData,
      );
      return { success: true, data: res.data };
    } catch (e) {
      return handleError<NumberPool>( 'Error trying to update number', e, 'Edge updateNumber');
    }
  };

  return {
    getNumbersAsync,
    createNumberAsync,
    deleteNumberAsync,
    updateNumberV3Async,
  };
}
